import classNames from "classnames";
import { Immutable } from "immer";
import React from "react";
import SVG from "react-inlinesvg";

import { constants } from "@reactivated/constants";

import iconUpsellModalSelectIcon from "../../../../img/icons/upsell-modal-select-icon.svg";
import iconUpsellModalSelectedIcon from "../../../../img/icons/upsell-modal-selected-icon.svg";
import { Flair } from "../../../components/Flair";
import { Image } from "../../../components/Image";
import { RatingGraphic } from "../../../components/RatingGraphic";
import { RichText } from "../../../components/RichText";
import { ValuePropsBar } from "../../../components/ValuePropsBar";
import { BundleGroupTypes } from "../../../constants";
import { useFlickity } from "../../../hooks/useFlickity";
import {
    IAddon,
    IConcreteBundle,
    IProduct,
    InternalProduct,
} from "../../../models/catalogue";
import { IProductID } from "../../../models/nominals";
import { IValuePropBarBlock } from "../../../models/value-props";
import {
    trackConfigUpsellAddedEvent,
    trackConfigUpsellRemovedEvent,
} from "../../../utils/analytics";
import { assertNever } from "../../../utils/never";
import { usePageSetting } from "../../../utils/settings";
import {
    IModularConfiguratorUpsellInfoModal,
    IModularConfigurator_UpsellInfoModalContent,
    IModularConfigurator_UpsellInfoModalProduct,
    IUpsellInfoModalFeaturesBlock,
    IUpsellInfoModalImageGallery,
    IUpsellInfoModalImageSlide,
    IUpsellInfoModalRichTextBlock,
    IUpsellInfoModalSegmentedRichTextBlock,
} from "../models";
import { getUpgradeID } from "../selectors";
import {
    buildUpsellPrice,
    buildUpsellProductName,
} from "../utils/upsellPricing";

import styles from "../ConfiguratorUpsellInfoModal/index.module.scss";

const AppSlug = constants["tsicommon.types.AppSlug"];
const TRADEMARK_SYMBOLS = new Set(["®", "™", "℠"]);

const UpsellInfoModalImageSlide = (props: {
    slide: IUpsellInfoModalImageSlide;
}) => {
    if (!props.slide.image) {
        return null;
    }
    const url =
        props.slide.image.renditions?.desktop_img?.url ?? props.slide.image.url;
    return (
        <Image
            className={styles.imageSlide}
            src={url}
            alt={props.slide.image.title}
            lazy={false}
        />
    );
};

const UpsellInfoModalImageGallery = (props: {
    block: IUpsellInfoModalImageGallery;
}): React.ReactNode => {
    const imageListElem = React.useRef<HTMLDivElement>(null);

    useFlickity(imageListElem, {
        options: {
            cellAlign: "left",
            pageDots: true,
            prevNextButtons: true,
            imagesLoaded: true,
        },
        enabled: props.block.slides.length > 1,
    });

    if (props.block.slides.length === 1) {
        const slide = props.block.slides[0];
        return (
            <Image
                className={styles.imageSlide}
                src={slide.image?.url}
                alt={slide.image?.title}
                lazy={false}
            />
        );
    }
    return (
        <div className={styles.imageGallery}>
            <div ref={imageListElem}>
                {props.block.slides.map((slide, idx) => (
                    <UpsellInfoModalImageSlide key={idx} slide={slide} />
                ))}
            </div>
        </div>
    );
};

const UpsellInfoModalFeaturesBlock = (props: {
    isOneProduct: boolean;
    block: IUpsellInfoModalFeaturesBlock;
}) => {
    return (
        <div>
            <h3 className={styles.featuresHeading}>{props.block.heading}</h3>
            <ul className={styles.featuresList}>
                {props.block.features.map((feature, idx) => (
                    <li key={idx} className={styles.featuresItem}>
                        {props.block.list_icon && (
                            <Image
                                className={styles.featuresIcon}
                                src={props.block.list_icon?.url}
                                alt=""
                            />
                        )}
                        <span>{feature.content}</span>
                    </li>
                ))}
            </ul>
        </div>
    );
};

const UpsellInfoModalSegmentedRichTextBlock = (props: {
    block: IUpsellInfoModalSegmentedRichTextBlock;
}) => {
    return (
        // TODO: Temporary CSS class to allow site-specific styling
        <div className={styles.segmentedRichText}>
            {props.block.segments.map((rich_text, idx) => {
                return <RichText key={idx} html={rich_text} />;
            })}
        </div>
    );
};

const UpsellInfoModalRichTextBlock = (props: {
    block: IUpsellInfoModalRichTextBlock;
}) => {
    return (
        <div className={styles.upsellRichTextBlock}>
            {props.block.heading && (
                <h3 className={styles.featuresHeading}>
                    {props.block.heading}
                </h3>
            )}
            {props.block.content && <RichText html={props.block.content} />}
        </div>
    );
};

const UpsellInfoModalValueProps = (props: { block: IValuePropBarBlock }) => {
    return (
        <div className={styles.valueProps}>
            <ValuePropsBar
                className="upsell-info-modal"
                valueProps={props.block.value_props}
            />
        </div>
    );
};

const containsTrademarkSymbol = (value: string): boolean => {
    for (const char of value) {
        if (TRADEMARK_SYMBOLS.has(char)) {
            return true;
        }
    }

    return false;
};

const UpsellProductName = ({
    product,
}: Immutable<{ product: InternalProduct }>) => {
    const title = buildUpsellProductName(product);
    if (!containsTrademarkSymbol(title)) {
        return title;
    }

    let chunk = "";
    const parts: React.ReactNode[] = [];
    // Render trademark symbols as superscript while preserving the original title text.
    for (const char of title) {
        if (TRADEMARK_SYMBOLS.has(char)) {
            if (chunk) {
                parts.push(chunk);
            }
            parts.push(<sup key={`${char}-${parts.length}`}>{char}</sup>);
            chunk = "";
            continue;
        }
        chunk += char;
    }

    if (chunk) {
        parts.push(chunk);
    }

    return <>{parts}</>;
};

const UpsellInfoModalBlock = (props: {
    isOneProduct: boolean;
    block: IModularConfigurator_UpsellInfoModalContent;
}) => {
    switch (props.block.type) {
        case "features":
            return (
                <UpsellInfoModalFeaturesBlock
                    isOneProduct={props.isOneProduct}
                    block={props.block.value}
                />
            );
        case "image_gallery":
            return <UpsellInfoModalImageGallery block={props.block.value} />;
        case "rich_text":
            return <UpsellInfoModalRichTextBlock block={props.block.value} />;
        case "segmented_rich_text":
            return (
                <UpsellInfoModalSegmentedRichTextBlock
                    block={props.block.value}
                />
            );
        case "value_props":
            return <UpsellInfoModalValueProps block={props.block.value} />;
    }
    assertNever(props.block);
    return null;
};

const UpsellInfoColumnProductInfo = (props: {
    product: IProduct;
    baseVariant: IProduct | null;
    selectedRoot: IProduct | null;
    bundleType: string;
    isOneProduct?: boolean | null;
}) => {
    const appSlug = usePageSetting("app_slug") || "";
    const buildImage = (product: InternalProduct) => {
        const productImage = product.parent
            ? product.parent.images[0]
            : product.images[0];

        if (productImage) {
            return productImage.original;
        }

        return "";
    };

    const ratingClasses = classNames({
        [styles.rating]: true,
        [styles.freeGiftRating]:
            props.bundleType === BundleGroupTypes.IN_CONFIGURATOR_FREE_GIFT,
    });

    const productNameClasses = classNames({
        [styles.productName]: true,
        [styles.freeGiftProductName]:
            props.bundleType === BundleGroupTypes.IN_CONFIGURATOR_FREE_GIFT,
    });

    const { price: upsellPrice, strikethroughPrice: upsellStrikethroughPrice } =
        buildUpsellPrice(props.product, props.baseVariant, {
            isFreeGift:
                props.bundleType === BundleGroupTypes.IN_CONFIGURATOR_FREE_GIFT,
            isRelative:
                props.bundleType === BundleGroupTypes.UPGRADE_PRODUCT ||
                props.bundleType === BundleGroupTypes.UPGRADE_MODEL,
            isOneProduct: props.isOneProduct ?? false,
        });

    return (
        <>
            <Image
                className={styles.productImage}
                src={buildImage(props.product)}
                alt=""
            />
            <h2 className={productNameClasses}>
                <UpsellProductName product={props.product} />
            </h2>
            {appSlug !== AppSlug.TEMPURCA &&
                props.product.rating &&
                props.product.rating > 0 && (
                    <RatingGraphic
                        starRatingClass={ratingClasses}
                        cardClass="tile"
                        cardSize="large"
                        numReviews={props.product.num_reviews}
                        rating={props.product.rating}
                    />
                )}
            {props.bundleType !==
                BundleGroupTypes.IN_CONFIGURATOR_FREE_GIFT && (
                <p className={styles.price}>
                    {upsellPrice}
                    {upsellStrikethroughPrice && (
                        <span className={styles.strikethroughPrice}>
                            {upsellStrikethroughPrice}
                        </span>
                    )}
                </p>
            )}
        </>
    );
};

export const UpsellInfoColumnContent = (props: {
    isOneProduct: boolean;
    content: readonly IModularConfigurator_UpsellInfoModalContent[];
}) => {
    return (
        <>
            {props.content.map((block) => (
                <UpsellInfoModalBlock
                    key={block.id}
                    isOneProduct={props.isOneProduct}
                    block={block}
                />
            ))}
        </>
    );
};

interface IColumnProps {
    column: IModularConfigurator_UpsellInfoModalProduct;
    isOneProduct: boolean;
    isFreeGift: boolean;
    bundleID: number;
    bundleType: string;
    configName: string;
    baseVariant: IProduct | null;
    selectedRoot: IProduct | null;
    selectedAddons: readonly IAddon[];
    selectedUpgrade: IProductID | null;
    upgradeBundles: readonly IConcreteBundle[];
    updateSelectedAddons: (
        updates: Array<{ productID: IProductID; quantity: number }>,
    ) => void;
    setSelectedUpgrade: (selectedUpgrade: IProductID | null) => void;
}

const ANALYTICS_CLASS_NAMES: { [key: string]: string } = {
    [BundleGroupTypes.IN_CONFIGURATOR_ADD_ON]:
        "al-add-on-card-panel-select-button",
    [BundleGroupTypes.UPGRADE_MODEL]:
        "al-change-model-upgrade-panel-select-button",
    [BundleGroupTypes.UPGRADE_PRODUCT]:
        "al-standard-upgrade-card-panel-select-button",
};

/**
 * Find the matching bundle and resolve the variant product if applicable.
 * Returns the variant from the bundle's suggested_products if one matches
 * the column product's parent, otherwise returns the column product directly.
 */
const resolveProductAndBundle = (
    columnProduct: IProduct | null,
    bundleID: number,
    upgradeBundles: readonly IConcreteBundle[],
): {
    product: IProduct | null;
    bundle: IConcreteBundle | null;
} => {
    if (!columnProduct) {
        return { product: null, bundle: null };
    }

    const thisBundle = upgradeBundles.find((b) => {
        return b.bundle_group.id === bundleID;
    });

    if (thisBundle) {
        const variantProduct = thisBundle.suggested_products.find((p) => {
            return p.parent?.id === columnProduct.id;
        });

        return {
            product: variantProduct || columnProduct,
            bundle: thisBundle,
        };
    }

    return {
        product: columnProduct,
        bundle: null,
    };
};

const checkIsSelected = (
    product: IProduct | null,
    bundleType: string,
    selectedRoot: IProduct | null,
    selectedUpgrade: IProductID | null,
    selectedAddons: readonly IAddon[],
): boolean => {
    const upgradeID = product ? getUpgradeID(product) : null;

    if (bundleType === BundleGroupTypes.UPGRADE_MODEL) {
        return selectedRoot !== null && selectedRoot.id === upgradeID;
    }

    if (bundleType === BundleGroupTypes.UPGRADE_PRODUCT) {
        return selectedUpgrade === upgradeID;
    }

    const selectedAddon = selectedAddons.find((addon) => {
        return addon.productID === upgradeID;
    });
    return selectedAddon !== undefined;
};

const UpsellInfoColumn = (props: IColumnProps): React.ReactNode => {
    // Resolve product and bundle synchronously from SSR-provided data
    const { product, bundle } = React.useMemo(
        () =>
            resolveProductAndBundle(
                props.column.product,
                props.bundleID,
                props.upgradeBundles,
            ),
        [props.column.product, props.bundleID, props.upgradeBundles],
    );

    // Capture initial baseVariant to use for price diff calculations
    const [baseVariant, setBaseVariant] = React.useState<IProduct | null>(null);
    const didCaptureBaseVariantRef = React.useRef(false);

    React.useEffect(() => {
        if (didCaptureBaseVariantRef.current) {
            return;
        }
        didCaptureBaseVariantRef.current = true;

        if (props.baseVariant) {
            setBaseVariant(props.baseVariant);
        }
    }, [props.baseVariant]);

    const onChange = (e: React.FormEvent<HTMLInputElement>) => {
        if (!product) {
            return;
        }

        // The root reducer discards an unavailable `setSelectedUpgrade`, but
        // this handler's other branch writes add-ons, which it does not cover.
        if (!product.availability.is_available_to_buy) {
            return;
        }
        const upgradeID = getUpgradeID(product);
        const isAdding = e.currentTarget.checked;

        // If this is an UPGRADE_PRODUCT or UPGRADE_MODEL Bundle, we update things one way...
        if (
            props.bundleType === BundleGroupTypes.UPGRADE_PRODUCT ||
            props.bundleType === BundleGroupTypes.UPGRADE_MODEL
        ) {
            props.setSelectedUpgrade(isAdding ? upgradeID : null);
            // If it's another kind of Bundle, we update things a different way...
        } else {
            const updates = [
                {
                    productID: upgradeID,
                    quantity: isAdding ? 1 : 0,
                },
            ];
            if (bundle) {
                for (const bundleProduct of bundle.suggested_products) {
                    const pID = getUpgradeID(bundleProduct);
                    if (pID !== upgradeID) {
                        updates.push({
                            productID: pID,
                            quantity: 0,
                        });
                    }
                }
            }
            props.updateSelectedAddons(updates);
        }
        if (isAdding) {
            trackConfigUpsellAddedEvent(props.configName);
        } else {
            trackConfigUpsellRemovedEvent(props.configName);
        }
    };

    const buildUI = () => {
        const upgradeID = product ? getUpgradeID(product) : null;

        if (
            // if the currently selected Root product matches the upgradeID,
            // AND this isn't an UPGRADE_MODEL bundle,
            // then don't show any UI
            props.selectedRoot &&
            props.bundleType !== BundleGroupTypes.UPGRADE_MODEL &&
            props.selectedRoot.id === upgradeID
        ) {
            return false;
        }

        const isSelected = checkIsSelected(
            product,
            props.bundleType,
            props.selectedRoot,
            props.selectedUpgrade,
            props.selectedAddons,
        );

        // Since #28993 an out-of-stock upgrade still reaches this modal (the
        // info button stays clickable), so the Select control has to refuse it
        // the same way the card's checkbox does.
        const isUnavailable =
            !!product && !product.availability.is_available_to_buy;

        // Reuse the server's per-site, admin-managed stock copy — the same
        // localized source the card's flair shows — so the disabled control
        // states a reason instead of just greying out.
        const textLabel = isUnavailable
            ? product.availability.short_message
            : isSelected
              ? "Selected"
              : "Select";
        const icon = isSelected ? (
            <SVG
                className={styles.selectedIcon}
                src={iconUpsellModalSelectedIcon}
            />
        ) : (
            <SVG
                className={styles.selectIcon}
                src={iconUpsellModalSelectIcon}
            />
        );
        const analyticsClassName = ANALYTICS_CLASS_NAMES[props.bundleType];
        const buttonClasses = classNames({
            [styles.selectButton]: true,
            [styles.selectedButton]: isSelected,
            [analyticsClassName]: true,
        });

        return (
            <label className={buttonClasses}>
                <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={onChange}
                    disabled={isUnavailable}
                />
                {icon}
                {textLabel}
            </label>
        );
    };

    const columnClasses = classNames({
        [styles.column]: true,
        [styles.multipleColumns]: !props.isOneProduct,
    });
    const headerClasses = classNames({
        [styles.columnHeader]: true,
        [styles.headerOneProduct]: props.isOneProduct && !props.isFreeGift,
        [styles.centeredHeader]: props.isFreeGift,
    });

    return (
        <div className={columnClasses}>
            <header className={headerClasses} role="group" aria-label="item">
                {props.column.flair && props.column.flair.length > 0 && (
                    <Flair flair={props.column.flair[0].value} inline={true} />
                )}
                {product && (
                    <UpsellInfoColumnProductInfo
                        product={product}
                        baseVariant={baseVariant}
                        selectedRoot={props.selectedRoot}
                        bundleType={props.bundleType}
                        isOneProduct={props.isOneProduct}
                    />
                )}
                <div>
                    {product &&
                        props.bundleType !==
                            BundleGroupTypes.IN_CONFIGURATOR_FREE_GIFT &&
                        buildUI()}
                </div>
            </header>
            <UpsellInfoColumnContent
                isOneProduct={props.isOneProduct}
                content={props.column.content}
            />
        </div>
    );
};

export const UpsellInfoModalContent = (props: {
    baseVariant: IProduct | null;
    selectedRoot: IProduct | null;
    modal: IModularConfiguratorUpsellInfoModal;
    selectedAddons: readonly IAddon[];
    selectedUpgrade: IProductID | null;
    upgradeBundles: readonly IConcreteBundle[];
    updateSelectedAddons: (
        updates: Array<{ productID: IProductID; quantity: number }>,
    ) => void;
    setSelectedUpgrade: (selectedUpgrade: IProductID | null) => void;
}): React.ReactNode => {
    return (
        <>
            <h1 className={styles.title}>{props.modal.bundle_description}</h1>
            <div className={styles.container}>
                {props.modal.upsell_info_products.map((column, i) => {
                    return (
                        <UpsellInfoColumn
                            key={`column-${i}`}
                            column={column}
                            isOneProduct={
                                props.modal.upsell_info_products.length < 2
                            }
                            isFreeGift={
                                props.modal.bundle_type ===
                                BundleGroupTypes.IN_CONFIGURATOR_FREE_GIFT
                            }
                            bundleID={props.modal.bundle}
                            bundleType={props.modal.bundle_type}
                            configName={
                                props.modal.bundle_headline ||
                                props.modal.bundle_description ||
                                ""
                            }
                            baseVariant={props.baseVariant}
                            selectedRoot={props.selectedRoot}
                            selectedAddons={props.selectedAddons}
                            selectedUpgrade={props.selectedUpgrade}
                            upgradeBundles={props.upgradeBundles}
                            updateSelectedAddons={props.updateSelectedAddons}
                            setSelectedUpgrade={props.setSelectedUpgrade}
                        />
                    );
                })}
            </div>
        </>
    );
};
